import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { StadiumDemoPlayer } from '@/features/marketing/components/StadiumDemoPlayer'

/**
 * /stadium/demo — full-screen interactive Stadium demo.
 *
 * Open to everyone: the demo visual at full viewport scale so a
 * visitor can actually feel what 1080p / 4K on a venue TV looks
 * like, with an "actually run one" CTA at the end and a soft return
 * path to the marketing page. There is no separate gated variant —
 * this page renders the same content for a search crawler as it
 * does for a first-time visitor, which keeps the route safe to
 * prerender for SEO without risking a cloaking penalty (serving a
 * different page to crawlers than to people).
 */
export default function StadiumDemo() {
  // Honest title bar update so the tab reads as the demo while open.
  // Restoring is best-effort: if the previous title was the
  // marketing page, this returns to it on unmount.
  useEffect(() => {
    const prev = document.title
    document.title = 'Stadium demo · e404'
    return () => {
      document.title = prev
    }
  }, [])

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-fg">
      {/* Top strip — escape hatch + branding. Intentionally not the
          full Nav: this is a focused viewing experience, not a
          navigation surface. */}
      <div className="flex items-center justify-between border-b border-border/60 bg-bg/70 px-6 py-3 backdrop-blur">
        <Link
          to="/explore/tournaments"
          className="font-display inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] text-fg-muted uppercase transition-colors hover:text-fg"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to tournaments
        </Link>
        <p className="font-display inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase">
          <Sparkles className="size-3" aria-hidden />
          Live Stadium demo
        </p>
      </div>

      {/* Demo player — drives the real Stadium phase components
          through the full tournament arc (Pre-event → Check-in →
          Live bracket → Grand final → Champion reveal) with the
          same crossfade animations as production. Player controls
          live under the viewport so the broadcast composition
          stays clean. */}
      <div className="flex flex-1 items-center justify-center bg-black/40 px-4 py-6">
        <div className="w-full max-w-[1280px]">
          <StadiumDemoPlayer />
          <p className="font-display mt-3 text-center text-[11px] tracking-[0.18em] text-fg-subtle uppercase">
            Auto-advances every 7 seconds · use the controls to scrub
          </p>
        </div>
      </div>

      {/* Closing CTA — the page that sold the visitor on the demo
          should also convert to signup. Keeps the demo focused: see
          it, decide. */}
      <div className="border-t border-border/60 bg-surface/40 px-6 py-10">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
          <h2 className="font-display text-balance text-2xl leading-tight font-bold md:text-3xl">
            Want this on your venue TV next Saturday?
          </h2>
          <p className="max-w-xl text-sm text-fg-muted">
            The Stadium opens automatically with every paid tournament.
            Create your squadron free, upgrade when you're ready to
            host one.
          </p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="primary" size="lg">
              <Link to="/signup">Get started free</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
